import type { ScopedRelationshipType, User, UserScopedRelationship } from '@/types';
import { getActiveScopedRelationships } from '@/lib/auth/scopedRelationshipResolver';

const RELATIONSHIP_CAPACITY_LABELS: Readonly<
  Partial<Record<ScopedRelationshipType, string>>
> = {
  company_representative: 'Investee Representative',
  project_manager: 'Project Manager',
  project_contributor: 'Project Contributor',
  partner_representative: 'Partner Representative',
  vendor_representative: 'Vendor Representative',
  investor_representative: 'Investor Representative',
  investment_manager: 'Investment Manager',
  reporting_recipient: 'Reporting Recipient',
  decision_participant: 'Decision Participant',
  observer: 'Project Observer',
};

function validatorCapacityLabel(relationship: UserScopedRelationship): string {
  if (relationship.resourceType === 'task') return 'Task Validator';
  if (relationship.resourceType === 'gate') return 'Gate Validator';
  if (relationship.resourceType === 'milestone') return 'Milestone Validator';
  return 'Validator';
}

/**
 * Presentation-only operating capacity for the application shell.
 *
 * Account roles remain the authority source. This label merely prevents a
 * scoped participant from being presented as the generic "Workspace Member"
 * while they are working in an assigned operating context.
 */
export function resolveScopedCapacityLabel(
  user: Pick<User, 'scopedRelationships'> | null | undefined,
): string | null {
  const labels = new Set<string>();

  for (const relationship of getActiveScopedRelationships(user)) {
    if (relationship.type === 'workspace_member') continue;
    const label = relationship.type === 'validator'
      ? validatorCapacityLabel(relationship)
      : RELATIONSHIP_CAPACITY_LABELS[relationship.type];
    if (label) labels.add(label);
  }

  if (labels.size === 0) return null;
  if (labels.size === 1) return labels.values().next().value ?? null;
  return 'Multiple scoped capacities';
}
